import { Doc } from '@dadohr/shared/domainTypes/document';
import { NOOP } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  AudienceWithCompletionCriteria,
  ContentTemplate,
  IMomentTemplate,
  LayoutBlock,
  SubTaskTemplate,
  TaskBlockTemplate
} from '@dadohr/shared/domainTypes/experience';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { Button, ListItemText, Menu, MenuItem } from '@material-ui/core';
import { ChevronDown } from 'lucide-react';
import { useRef, useState } from 'react';

import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { useMemo } from 'react';
import { TokenDefinition } from '../../../../../../components/MarkdownText';
import {
  UnsafeTFunction,
  useUnsafeTranslation
} from '../../../../../../i18next';
import { getAudienceName } from '../../../../../../services/experiences/audience';
import { useProductAnalytics } from '../../../../../../services/productAnalytics';
import {
  BlockDefinition,
  BlockSelector,
  BlockSettings,
  BlockSettingsReadOnly
} from '../../../../../Experiences/components/Builders/TaskBuilder';
import { TaskBuilderContext } from '../../../../../Experiences/components/Builders/TaskBuilder/types';
import { TaskBuilderBlockValidationState } from '../../../../../Experiences/components/Builders/TaskBuilder/validation';
import { getBlocksForMomentAudience } from '../../../../services';

type BlockListPropsEdit = {
  context: TaskBuilderContext;
  org: IOrganization;
  selectedBlock: TaskBlockTemplate | null;
  template: Doc<IMomentTemplate>;
  validation: TaskBuilderBlockValidationState;
  setValidation: React.Dispatch<
    React.SetStateAction<TaskBuilderBlockValidationState>
  >;
  onDeselectBlock: () => void;
  onChangeBlock: (nextBlock: TaskBlockTemplate) => void;
  contentBlocks: BlockDefinition<ContentTemplate | LayoutBlock>[];
  subTaskBlocks: BlockDefinition<SubTaskTemplate>[];

  selectedAudience: string | null;
  onCopyAllBlocksFromOneAudienceToAnother: (
    sourceAudienceId: string,
    destinationAudienceId: string
  ) => void;

  tokens: TokenDefinition[];
};

type BlockListPropsView = Pick<
  BlockListPropsEdit,
  'context' | 'org' | 'selectedBlock' | 'onDeselectBlock' | 'tokens'
>;

export const getCopyContentSourceOptions = (
  audiences: AudienceWithCompletionCriteria[],
  currentAudienceId: string | null,
  t: UnsafeTFunction
) => {
  const hasOtherAudiences = audiences.length > 1;
  return audiences
    .filter((a) => a.id !== currentAudienceId && a.blocks.length)
    .map((audience) => ({
      audienceId: audience.id,
      label: t('{{ audienceName}}', {
        audienceName: getAudienceName(audience, hasOtherAudiences, t)
      })
    }));
};

export const BlockList = ({
  context,
  org,
  selectedBlock,
  template,
  validation,
  setValidation,
  onDeselectBlock,
  onChangeBlock,
  contentBlocks,
  subTaskBlocks,
  selectedAudience,
  onCopyAllBlocksFromOneAudienceToAnother,
  tokens
}: BlockListPropsEdit) => {
  const { t } = useUnsafeTranslation();
  const { trackEvent } = useProductAnalytics();
  const { audiences, blocks } = template.data.content;

  const copyContentSourceOptions = getCopyContentSourceOptions(
    audiences,
    selectedAudience,
    t
  );
  const blockCount = useMemo(() => {
    const audience = audiences.find((a) => a.id === selectedAudience);
    return getBlocksForMomentAudience(audience, blocks).length;
  }, [selectedAudience, blocks, audiences]);

  return (
    <div>
      {selectedBlock ? (
        <BlockSettings
          blocks={template.data.content.blocks}
          anchorFields={[]}
          b={selectedBlock}
          customFields={org.customUserFields}
          expTpl={context}
          resources={[]}
          setValidation={setValidation ?? NOOP}
          validation={
            selectedBlock && validation ? validation[selectedBlock.id] : null
          }
          onBack={onDeselectBlock}
          onChangeBlock={onChangeBlock ?? NOOP}
          onChangeResources={NOOP}
          showOnCompleteHookSettings={false}
          hideOptionalSubTaskSetting={true}
          tokens={tokens}
          actor={'NO_ACTOR'} // Not sure what we should say here - this is needed for e.g. DocuSign, but it's a concept that's not relevant for Moments. *It should not be possible to use such a block within Moments*
          channels={[]} // Moments don't have channels - this is only relevant for tasks
        />
      ) : (
        <BlockSelector
          contentBlocks={contentBlocks ?? []}
          subTaskBlocks={subTaskBlocks ?? []}
          copyContent={
            !!copyContentSourceOptions.length && (
              <CopyAudienceContentButton
                selectedAudienceId={selectedAudience || ''}
                options={copyContentSourceOptions}
                onCopyAllBlocksFromOneAudienceToAnother={(
                  sourceAudienceId,
                  destinationAudienceId
                ) => {
                  const sourceAudience = template.data.content.audiences.find(
                    (a) => a.id === sourceAudienceId
                  );
                  const destinationAudience = template.data.content.audiences.find(
                    (a) => a.id === destinationAudienceId
                  );

                  // Should never happen
                  if (!sourceAudience) return;
                  if (!destinationAudience) return;

                  // Always true; copy content button is disabled if there’s just one audience
                  const hasOtherAudiences = true;

                  trackEvent({
                    eventType: PAEventType.OTHER,
                    name: 'Moment Content Copied',
                    Explanation:
                      'Admin copied content for a moment from one audience to another',
                    'Moment ID': template.id,
                    'Moment Name': template.data.title,
                    'Selected Target Audience': getAudienceName(
                      destinationAudience,
                      hasOtherAudiences,
                      t
                    ),
                    'Copied Target Audience': getAudienceName(
                      sourceAudience,
                      hasOtherAudiences,
                      t
                    ),
                    'Content Empty': blockCount === 0
                  });

                  onCopyAllBlocksFromOneAudienceToAnother(
                    sourceAudienceId,
                    destinationAudienceId
                  );
                }}
              />
            )
          }
        />
      )}
    </div>
  );
};

export const BlockListReadOnly = ({
  context,
  org,
  tokens,
  selectedBlock,
  onDeselectBlock
}: BlockListPropsView) => {
  if (!selectedBlock) {
    return null;
  }

  return (
    <div>
      <BlockSettingsReadOnly
        anchorFields={[]}
        b={selectedBlock}
        customFields={org.customUserFields}
        expTpl={context}
        resources={[]}
        validation={null}
        onBack={onDeselectBlock}
        showOnCompleteHookSettings={false}
        tokens={tokens}
      />
    </div>
  );
};

export const CopyAudienceContentButton = ({
  selectedAudienceId,
  options: copySourceOptions,
  onCopyAllBlocksFromOneAudienceToAnother
}: {
  selectedAudienceId: string;
  options: { audienceId: string; label: string }[];
  onCopyAllBlocksFromOneAudienceToAnother?: (
    sourceAudienceId: string,
    destinationAudienceId: string
  ) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outlined"
        endIcon={<ChevronDown size={16} />}
        onClick={() => setOpen(true)}
        ref={buttonRef}
      >
        {t('Copy Content')}
      </Button>
      <Menu
        getContentAnchorEl={null}
        id="experience-more-menu"
        anchorEl={buttonRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        {copySourceOptions.map((s) => (
          <MenuItem
            onClick={() => {
              onCopyAllBlocksFromOneAudienceToAnother?.(
                s.audienceId,
                selectedAudienceId
              );
              setOpen(false);
            }}
          >
            <ListItemText>{s.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
